import { Component, EventEmitter, Output, OnInit } from '@angular/core';
import { CircleSelectionConfig, RadiusUnit } from '../../model/map/circle-selection-config';

/**
 * Circle Selection Panel Component
 * Provides UI for configuring circle radius and unit before placement
 * Part of the new Circle Selection Tool feature
 */
@Component({
    selector: 'app-circle-selection-panel',
    templateUrl: './circle-selection-panel.component.html',
    styleUrls: ['./circle-selection-panel.component.scss']
})
export class CircleSelectionPanelComponent implements OnInit {
    
    // Expose enum to template
    RadiusUnit = RadiusUnit;
    
    // Configuration properties
    radiusValue: number = 500;
    radiusUnit: RadiusUnit = RadiusUnit.KILOMETERS;
    isCircleExpanded: boolean = false;
    
    // Available radius units for dropdown
    radiusUnits = [
        { value: RadiusUnit.MILES, label: 'Miles' },
        { value: RadiusUnit.KILOMETERS, label: 'Kilometers' },
        { value: RadiusUnit.METERS, label: 'Meters' },
        { value: RadiusUnit.FEET, label: 'Feet' }
    ];
    
    // Event emitters
    @Output() circleConfigured = new EventEmitter<CircleSelectionConfig>();
    @Output() polygonClicked = new EventEmitter<void>();
    @Output() rectangleClicked = new EventEmitter<void>();
    @Output() polygonNewClicked = new EventEmitter<void>();
    @Output() rectangleNewClicked = new EventEmitter<void>();
    @Output() cancelClicked = new EventEmitter<void>();
    @Output() clearAllCircles = new EventEmitter<void>();
    
    ngOnInit(): void {
        // Initialize with default values
    }
    
    /**
     * Toggle circle configuration panel
     */
    toggleCircleConfig(): void {
        this.isCircleExpanded = !this.isCircleExpanded;
    }
    
    /**
     * Handle radius unit change
     */
    onUnitChange(unit: RadiusUnit): void {
        this.radiusUnit = unit;
    }
    
    /**
     * Handle radius value change
     */
    onRadiusChange(value: number): void {
        this.radiusValue = value;
    }
    
    /**
     * Validate radius input
     */
    isValidRadius(): boolean {
        return this.radiusValue > 0 && !isNaN(this.radiusValue);
    }
    
    /**
     * Convert radius to meters (Google Maps standard)
     */
    convertToMeters(value: number, unit: RadiusUnit): number {
        switch (unit) {
            case RadiusUnit.METERS:
                return value;
            case RadiusUnit.KILOMETERS:
                return value * 1000;
            case RadiusUnit.MILES:
                return value * 1609.34;
            case RadiusUnit.FEET:
                return value * 0.3048;
            default:
                return value;
        }
    }
    
    /**
     * Handle Create Circle button click
     */
    onCreateCircle(): void {
        if (!this.isValidRadius()) {
            return;
        }
        
        const config: CircleSelectionConfig = {
            radiusValue: this.radiusValue,
            radiusUnit: this.radiusUnit,
            radiusInMeters: this.convertToMeters(this.radiusValue, this.radiusUnit)
        };
        
        // Emit configuration to parent
        this.circleConfigured.emit(config);
        
        // Close the panel
        this.isCircleExpanded = false;
    }
    
    /**
     * Handle Polygon button click (placeholder)
     */
    onPolygonClick(): void {
        this.polygonClicked.emit();
    }
    
    /**
     * Handle Rectangle button click (placeholder)
     */
    onRectangleClick(): void {
        this.rectangleClicked.emit();
    }

    /**
     * Handle "Polygon New" button click.
     * Click-to-add-vertex capture on plain map events, with no
     * google.maps.drawing.DrawingManager involved.
     */
    onPolygonNewClick(): void {
        this.isCircleExpanded = false;
        this.polygonNewClicked.emit();
    }

    /**
     * Handle "Rectangle New" button click.
     * Drag-to-draw capture on plain map events, with no
     * google.maps.drawing.DrawingManager involved.
     */
    onRectangleNewClick(): void {
        this.isCircleExpanded = false;
        this.rectangleNewClicked.emit();
    }
    
    /**
     * Task 7: Handle Clear All Circles button click
     */
    onClearAllCircles(): void {
        this.clearAllCircles.emit();
        this.isCircleExpanded = false;
    }
    
    /**
     * Reset radius value and unit to defaults
     */
    resetToDefaults(): void {
        this.radiusValue = 500;
        this.radiusUnit = RadiusUnit.KILOMETERS;
    }
    
    /**
     * Handle Cancel button click
     * Task 7: Also clears all circles
     */
    onCancelClick(): void {
        this.isCircleExpanded = false;
        
        // Clear all circles before canceling
        this.clearAllCircles.emit();
        
        // Reset radius to defaults
        this.resetToDefaults();
        
        // Then emit cancel event
        this.cancelClicked.emit();
    }
}
